import { useDispatch } from "react-redux";
import {
  savePlanRequest,
  updatePlanRequest,
  saveDayPlanRequest,
  saveDayPlanDetailRequest,
  updateDayPlanRequest,
  updateDayPlanDetailRequest,
  deletePlanRequest ,
  deleteDayPlanDetailRequest
} from "../../../store/Plan/planActions";
import { useState } from "react";
import { fetchChurchesRequest } from "../../../store/Churches/actions";

export const AllPlansHooks = () => {
  const dispatch = useDispatch();
  const [reloadData, setReloadData] = useState(false);

  const fetchAllPlans = (page, limit, searchCriteria = {}, callbackFn, churchId = "", typeonplan, typeofentity) => {
    dispatch(
      fetchChurchesRequest({
        apiPayloadRequest: {
          page: page,
          limit: limit,
          searchCriteria: searchCriteria,
          _id: churchId,
          typeofentity: typeofentity,
        },
        callback: (Data) => {
          if (Data?.type === 1) {
            let allPlans = [];

            Data?.data?.forEach(church => {
              if (church.plans && Array.isArray(church.plans)) {
                let churchPlans = church.plans.map((plan) => {
                  // Handle potentially empty object for dates
                  const rawDate = plan.createdDate;
                  let formattedDate = "";

                  if (rawDate && typeof rawDate === 'object' && Object.keys(rawDate).length === 0) {
                    formattedDate = ""; // It's an empty object {}
                  } else if (rawDate) {
                    formattedDate = rawDate;
                  }

                  return {
                    ...plan,
                    churchId: church._id,
                    churchName: church.churchName,
                    planObjId: plan._id,
                    planName: plan.planName || "Untitled Plan",
                    planDescription: plan.description || "",
                    noOfDays: plan.noofDays || "0",
                    galleryObjId: plan.galleryObjId,
                    gallery: plan.gallery,
                    createdDate: formattedDate,
                    typeonplan: plan.typeonplan,
                    userAuthId: plan.userAuthId,
                    expiryDate: plan.expiryDate,
                    dayPlans: plan.dayplans || []
                  };
                });

                if (typeonplan) {
                  churchPlans = churchPlans.filter(plan => Number(plan.typeonplan) === Number(typeonplan));
                }

                allPlans = [...allPlans, ...churchPlans];
              }
            });

            if (searchCriteria.searchText) {
              const searchText = searchCriteria.searchText.toLowerCase();
              allPlans = allPlans.filter(plan =>
                (plan.planName && plan.planName.toLowerCase().includes(searchText)) ||
                (plan.planDescription && plan.planDescription.toLowerCase().includes(searchText)) ||
                (plan.churchName && plan.churchName.toLowerCase().includes(searchText))
              );
            }

            const startIndex = (page - 1) * limit;
            const endIndex = startIndex + limit;
            const paginatedPlans = allPlans.slice(startIndex, endIndex);

            if (callbackFn) {
              callbackFn({
                data: paginatedPlans,
                total: allPlans.length,
                page: page,
                limit: limit
              });
            }
          } else if (callbackFn) {
            callbackFn({
              data: [],
              total: 0,
              page: page,
              limit: limit
            });
          }
        },
      })
    );
  };

  return { reloadData, setReloadData, fetchAllPlans, dispatch };
};

// Simple function to extract dayplan ID from response
const extractDayPlanId = (response) => {

  if (!response) {
        return null;
  }

  // First check for dayplanObjId at root level (this is what your API returns)
  if (response.dayplanObjId) {
        return response.dayplanObjId;
  }

  // Check for _id at root level
  if (response._id) {
        return response._id;
  }

  // Check inside data object if it exists
  if (response.data) {
    // If data is an object with dayplanObjId
    if (response.data.dayplanObjId) {
            return response.data.dayplanObjId;
    }

    // If data is an object with _id
    if (response.data._id) {
            return response.data._id;
    }

    // If data is an array with first item having _id
    if (Array.isArray(response.data) && response.data.length > 0 && response.data[0]._id) {
            return response.data[0]._id;
    }
  }

      return null;
};

// Helper function to save or update day plan details
const saveOrUpdateDayPlanDetails = async (dispatch, planObjId, dayplanObjId, dayPlan, dayIndex, isUpdate = false, existingDetails = []) => {
  const detailResults = [];

  // If there are no verses but only YouTube URL, create a single detail with YouTube URL
  if (dayPlan.youtubeVideoUrl && dayPlan.youtubeVideoUrl.trim() !== "" &&
      (!dayPlan.verseObjId || !Array.isArray(dayPlan.verseObjId) || dayPlan.verseObjId.length === 0)) {

    const youtubePayload = {
      _id: planObjId,
      dayplanObjId: dayplanObjId,
      youtubevideourl: dayPlan.youtubeVideoUrl.trim(),
      versionid: "",
      bookId: "",
      chapterId: "",
      verseStart: "",
      bookName: "",
      verseText: ""
    };


    const youtubeResult = await new Promise((resolve) => {
      if (isUpdate && existingDetails[0]?._id) {
        // Update existing YouTube detail
        dispatch(updateDayPlanDetailRequest({
          ...youtubePayload,
          dayplandetailObjId: existingDetails[0]._id
        }, (detailRes) => {
                    resolve({
            type: detailRes?.type || 0,
            message: detailRes?.message || "No response",
            success: Number(detailRes?.type) === 1,
            detailType: "youtube-only"
          });
        }));
      } else {
        // Save new YouTube detail
        dispatch(saveDayPlanDetailRequest(youtubePayload, (detailRes) => {
                    resolve({
            type: detailRes?.type || 0,
            message: detailRes?.message || "No response",
            success: Number(detailRes?.type) === 1,
            detailType: "youtube-only"
          });
        }));
      }
    });

    detailResults.push(youtubeResult);
  }

  // Save each verse as a separate detail (include YouTube URL in the first verse if available)
  if (dayPlan.verseObjId && Array.isArray(dayPlan.verseObjId) && dayPlan.verseObjId.length > 0) {

    for (let verseIndex = 0; verseIndex < dayPlan.verseObjId.length; verseIndex++) {
      const verse = dayPlan.verseObjId[verseIndex];

      // Prepare verse payload - include YouTube URL only in the first verse
      const versePayload = {
        _id: planObjId,
        dayplanObjId: dayplanObjId,
        youtubevideourl: (verseIndex === 0 && dayPlan.youtubeVideoUrl && dayPlan.youtubeVideoUrl.trim() !== "")
          ? dayPlan.youtubeVideoUrl.trim()
          : "",
        versionid: verse.filesetid || "",
        bookId: verse.book_id || "",
        chapterId: verse.chapter?.toString() || "",
        verseStart: verse.verse_start?.toString() || "",
        bookName: verse.book_name || "",
        verseText: verse.text || ""
      };


      const verseResult = await new Promise((resolve) => {
        if (isUpdate && existingDetails[verseIndex]?._id) {
          // Update existing verse detail
          dispatch(updateDayPlanDetailRequest({
            ...versePayload,
            dayplandetailObjId: existingDetails[verseIndex]._id
          }, (detailRes) => {
                        resolve({
              type: detailRes?.type || 0,
              message: detailRes?.message || "No response",
              success: Number(detailRes?.type) === 1,
              detailType: "verse",
              verseIndex: verseIndex + 1,
              verseReference: `${verse.book_name} ${verse.chapter}:${verse.verse_start}`
            });
          }));
        } else {
          // Save new verse detail
          dispatch(saveDayPlanDetailRequest(versePayload, (detailRes) => {
                        resolve({
              type: detailRes?.type || 0,
              message: detailRes?.message || "No response",
              success: Number(detailRes?.type) === 1,
              detailType: "verse",
              verseIndex: verseIndex + 1,
              verseReference: `${verse.book_name} ${verse.chapter}:${verse.verse_start}`
            });
          }));
        }
      });

      detailResults.push(verseResult);

      // Small delay between verse saves to avoid overwhelming the server
      if (verseIndex < dayPlan.verseObjId.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
  }

  return detailResults;
};

// Save plan function
export const handleSavePlan = async (dispatch, planData, galleryObjId, churchObjId, dayPlans, callback, typeofentity) => {

  const getEntityKey = (type) => {
    if (Number(type) === 2) return "channelObjId";
    if (Number(type) === 3) return "ministriesObjId";
    return "churchObjId";
  };

  const entityKey = getEntityKey(typeofentity);

  const payload = {
    ...planData,
    typeonplan: 1
  };

  if (galleryObjId) {
    if (typeof galleryObjId === 'string' && galleryObjId.startsWith('http')) {
      payload.coverImage = galleryObjId;
      payload.plansCoverImageUrl = galleryObjId;
      payload.imageUrl = galleryObjId;
      payload.logourl = galleryObjId;
    } else {
      payload.galleryObjId = galleryObjId;
    }
  }

  if (churchObjId) {
    payload[entityKey] = churchObjId;
  }

  // 1. Save the main plan
  dispatch(savePlanRequest(payload, async (res) => {

    if (Number(res?.type) !== 1) {
            callback?.({ type: 0, message: res?.message || "Failed to save main plan" });
      return;
    }

    // ROBUST ID EXTRACTION
    let planObjId = null;
    if (res?.data) {
        if (Array.isArray(res.data) && res.data.length > 0) {
            planObjId = res.data[0]._id || res.data[0].planObjId;
        } else if (typeof res.data === 'object') {
            planObjId = res.data._id || res.data.planObjId;
        }
    }

    if (!planObjId) {
        planObjId = res?._id || res?.planObjId;
    }


    if (!planObjId) {
            callback?.({ type: 0, message: "No plan ID received from server" });
      return;
    }

    // 2. Save each day plan sequentially
    const dayPlanResults = [];

    if (!dayPlans || !Array.isArray(dayPlans) || dayPlans.length === 0) {
                callback?.({
            type: 1,
            message: "Plan saved successfully (no day plans were provided).",
            data: { planObjId }
        });
        return;
    }

    for (let dayIndex = 0; dayIndex < dayPlans.length; dayIndex++) {
      const day = dayPlans[dayIndex];

      const dayPlanBasicPayload = {
        _id: planObjId,
        dayNumber: day.dayNumber || (dayIndex + 1),
        dayplanname: day.dayPlanName || `Day ${dayIndex + 1}`,
        dayplandescription: day.dayPlanDescription || "",
      };

      // 🔐 SAVE DAY PLAN
      await new Promise((resolveDay) => {
                dispatch(
          saveDayPlanRequest(dayPlanBasicPayload, async (saveRes) => {

            if (Number(saveRes?.type) !== 1) {
                            dayPlanResults.push({ day: dayIndex + 1, success: false });
              resolveDay();
              return;
            }

            const dayplanObjId = saveRes.dayplanObjId || saveRes._id;

            if (!dayplanObjId) {
                                dayPlanResults.push({ day: dayIndex + 1, success: false });
                resolveDay();
                return;
            }

            let daySuccess = true;

            // 🔒 SAVE DETAILS
            const verses = day.verseObjId || [];

            if (verses.length === 0) {
                // If no verses but has YouTube URL, save one detail
                if (day.youtubeVideoUrl) {
                                        await new Promise((resolveDetail) => {
                        dispatch(
                          saveDayPlanDetailRequest({
                            _id: planObjId,
                            dayplanObjId,
                            youtubevideourl: day.youtubeVideoUrl,
                            versionid: "",
                            bookId: "",
                            chapterId: "",
                            verseStart: "",
                            bookName: "",
                            verseText: ""
                          }, (detRes) => {
                            if (Number(detRes?.type) !== 1) {
                                                                daySuccess = false;
                            }
                            resolveDetail();
                          })
                        );
                      });
                }
            } else {
                for (let v = 0; v < verses.length; v++) {
                  const verse = verses[v];

                  const detailPayload = {
                    _id: planObjId,
                    dayplanObjId,
                    bookId: verse.book_id || "",
                    bookName: verse.book_name || "",
                    chapterId: String(verse.chapter || ""),
                    verseStart: String(verse.verse_start || ""),
                    verseText: verse.text || "",
                    versionid: verse.filesetid || "",
                    youtubevideourl: (v === 0) ? (day.youtubeVideoUrl || "") : "",
                  };

                                    await new Promise((resolveDetail) => {
                    dispatch(
                      saveDayPlanDetailRequest(detailPayload, (detRes) => {
                        if (Number(detRes?.type) !== 1) {
                                                        daySuccess = false;
                        }
                        resolveDetail();
                      })
                    );
                  });
                }
            }

            dayPlanResults.push({ day: dayIndex + 1, success: daySuccess });
            resolveDay();
          })
        );
      });
    }


    const failedDays = dayPlanResults.filter(result => !result.success);

    if (failedDays.length > 0) {
      callback?.({
        type: 1,
        message: `Plan saved, but ${failedDays.length} day(s) encountered issues saving details.`,
        data: { planObjId, dayPlanResults }
      });
    } else {
      callback?.({
        type: 1,
        message: "Plan saved successfully with all day plans!",
        data: { planObjId, dayPlanResults }
      });
    }
  }));
};
const getNewVerses = (incomingVerses = [], existingDetails = []) => {
  const existingVerseIds = existingDetails
    .filter(d => d.verseObjId)
    .map(d => String(d.verseObjId));

  return incomingVerses.filter(v => !existingVerseIds.includes(String(v)));
};

// Update plan function with updateDayPlanDetailRequest implementation
export const handleUpdatePlan = async (
  dispatch,
  rowData,
  planData,
  galleryObjId,
  dayPlans,
  callback,
  typeofentity
) => {
  const planObjId = rowData?.planObjId || rowData?._id;

  if (!planObjId) {
    callback?.({ type: 0, message: "Plan ID missing" });
    return;
  }

  const getEntityKey = (type) => {
    if (Number(type) === 2) return "channelObjId";
    if (Number(type) === 3) return "ministriesObjId";
    return "churchObjId";
  };

  const entityKey = getEntityKey(typeofentity);

  const payload = {
    _id: planObjId,
    planName: planData.planName,
    description: planData.description,
    noofDays: planData.noofDays,
    categoryObjId: planData.categoryObjId,
    layoutStyle: planData.layoutStyle
  };

  if (galleryObjId) {
    if (typeof galleryObjId === 'string' && galleryObjId.startsWith('http')) {
      payload.coverImage = galleryObjId;
      payload.plansCoverImageUrl = galleryObjId;
      payload.imageUrl = galleryObjId;
      payload.logourl = galleryObjId;
    } else {
      payload.galleryObjId = galleryObjId;
    }
  }

  const churchId = rowData.churchId || planData.churchObjId;
  if (churchId) {
    payload[entityKey] = churchId;
  }

  // 1️⃣ Update main plan
  dispatch(updatePlanRequest(payload, async (res) => {

    if (Number(res.type) !== 1) {
      callback?.({ type: 0, message: res.message });
      return;
    }

    const results = [];

    // 2️⃣ Day Plans loop
    for (let i = 0; i < dayPlans.length; i++) {
      const incomingDay = dayPlans[i];
      const existingDay = rowData?.dayPlans?.[i];

      let dayplanObjId = existingDay?._id;

      try {
        // 🔹 UPDATE EXISTING DAY
        if (dayplanObjId) {
          await new Promise(resolve => {
            dispatch(updateDayPlanRequest({
              _id: planObjId,
              dayplanObjId,
              dayNumber: incomingDay.dayNumber || i + 1,
              dayplanname: incomingDay.dayPlanName,
              dayplandescription: incomingDay.dayPlanDescription
            }, resolve));
          })
        }
        // 🔹 CREATE NEW DAY
        else {
          const saveRes = await new Promise(resolve => {
            dispatch(saveDayPlanRequest({
              _id: planObjId,
              dayNumber: incomingDay.dayNumber || i + 1,
              dayplanname: incomingDay.dayPlanName,
              dayplandescription: incomingDay.dayPlanDescription
            }, resolve));
          });

          if (Number(saveRes.type) !== 1) {
            results.push({ day: i + 1, success: false });
            continue;
          }

          dayplanObjId = saveRes.dayplanObjId || saveRes._id;
        }

        // 3️⃣ DETAILS (ONLY NEW)
        const existingDetails = existingDay?.dayPlanDetails || [];

        const newVerses = getNewVerses(
          incomingDay.verseObjId,
          existingDetails
        );

        const shouldSaveYoutube =
          incomingDay.youtubeVideoUrl &&
          !existingDetails.some(
            d => d.youtubeVideoUrl === incomingDay.youtubeVideoUrl
          );

        if (newVerses.length || shouldSaveYoutube) {
          await saveOrUpdateDayPlanDetails(
            dispatch,
            planObjId,
            dayplanObjId,
            {
              ...incomingDay,
              verseObjId: newVerses,
              youtubeVideoUrl: shouldSaveYoutube
                ? incomingDay.youtubeVideoUrl
                : ""
            },
            i,
            false
          );
        }

        results.push({ day: i + 1, success: true });

      } catch (err) {
        results.push({ day: i + 1, success: false, error: err.message });
      }
    }

    callback?.({
      type: 1,
      message: "Plan updated successfully",
      data: results
    });
  }));
};

// Add this function in PlanHooks.js
export const handleDeleteDayPlanDetail = (dispatch, planObjId, dayplanObjId, dayplandetObjId, callback) => {

  const payload = {
    _id: planObjId,
    dayplanObjId: dayplanObjId,
    dayplandetObjId: dayplandetObjId
  };

  // You'll need to add this action in your planActions file
  dispatch(deleteDayPlanDetailRequest(payload, callback));
};
export const handleDeletePlan = (dispatch, churchId, planObjId, callback) => {
    dispatch(deletePlanRequest({ _id: planObjId }, callback));
};
